import { diff } from 'deep-object-diff';

import { UnknownObject } from '../types';

/**
 * Filters out fields with empty values (null, undefined, '') from an object.
 * Optionally skips certain keys from being filtered.
 *
 * @template T The type of the input object.
 * @param {T} obj The object to filter.
 * @param {(keyof T)[]} [skipKeys=[]] An optional array of keys to exclude from filtering.
 * @returns {Partial<T>} A new object containing only the fields with non-empty values,
 * or including skipped keys regardless of their value.
 */
export const filterEmptyFields = <T extends Record<string, any>>(
  obj: T,
  skipKeys: (keyof T)[] = []
): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([key, value]) => skipKeys.includes(key as keyof T) || Boolean(value)
    )
  ) as Partial<T>;

/**
 * Calculates the shallow difference between two objects.
 * It returns a new object containing only the keys where the values differ in `obj2` compared to `obj1`.
 * Only the values from `obj2` for the differing keys are included in the result.
 *
 * @template T The type of the input objects.
 * @param {T} obj1 The first object.
 * @param {T} obj2 The second object.
 * @returns {UnknownObject} A new object containing the keys with differing values from `obj2`.
 */
export const shallowDifference = <T extends Record<string, any>>(
  obj1: T,
  obj2: T
): UnknownObject =>
  Object.keys(diff(obj1, obj2)).reduce(
    (acc, key) => ({ ...acc, [key]: obj2[key] }),
    {}
  );

/**
 * Retrieves a value from an object based on a given path.
 * The path can be a simple key or a nested path separated by dots (e.g., 'a.b.c').
 *
 * @param {{ [field: string]: any }} obj The object to retrieve the value from.
 * @param {string} name The path to the desired value.
 * @returns {any} The value at the specified path, or undefined if the path does not exist.
 */
export const getValueByPath = (
  obj: { [field: string]: any },
  name: string
): any =>
  name.includes('.')
    ? name
        .split('.')
        .reduce((childObj, fieldName) => childObj?.[fieldName], obj)
    : obj[name];

/**
 * Creates a new object containing only the specified fields from the input object.
 * It also filters out any fields where the value is falsy (null, undefined, 0, '', false).
 *
 * @template T The type of the input object.
 * @param {T} values The object to pick fields from.
 * @param {(keyof T)[]} fieldsNames An array of field names to include in the new object.
 * @returns {T} A new object containing only the specified fields with truthy values.
 */
export const pick = <T extends Record<string, any> = Record<string, any>>(
  values: T,
  fieldsNames: (keyof T)[]
): T =>
  Object.fromEntries(
    fieldsNames
      .map((fieldName) => [fieldName, values[fieldName]])
      .filter((entry) => Boolean(entry[1]))
  );

/**
 * Replaces all occurrences of a specific value within the fields of an object with a new value.
 *
 * @template T The type of the input object.
 * @param {T} values The object whose field values need to be replaced.
 * @param {object} options An object containing the value to replace and the new value.
 * @param {any} options.value The value to be replaced.
 * @param {any} options.replacer The new value to replace with.
 * @returns {T} A new object with the specified values replaced.
 */
export const replaceFieldsValue = <T extends UnknownObject>(
  values: T,
  options: { value: any; replacer: any }
) => {
  const payload = { ...values };
  Object.entries(payload).map(([key, value]) => {
    if (value === options.value) {
      payload[key as keyof T] = options.replacer;
    }
  });
  return payload;
};
